import { useState, useEffect } from "react";
import { useI18n } from "@/lib/i18n";
import { useInView } from "@/hooks/useInView";


const taglines = [
  { key: "whowe.tagline1" },
  { key: "whowe.tagline2" },
];

const WhoWeAreSection = () => {
  const { t, lang } = useI18n();
  const [currentTagline, setCurrentTagline] = useState(0);
  const [lineRef, lineInView] = useInView();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTagline((prev) => (prev + 1) % taglines.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="whowe" className="relative overflow-hidden">
      {/* ── Video Banner ── */}
      <div className="relative w-full h-[60vh] sm:h-[70vh] md:h-[80vh] lg:h-[85vh] overflow-hidden">
        <div className="absolute inset-0">
          <video
            src="/videos/adwar-bg.mp4"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
            style={{ backfaceVisibility: "hidden", transform: "translateZ(0)" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>

        <div className="absolute inset-0 bg-gradient-to-b from-navy/40 via-navy/60 to-navy opacity-60" />

        {/* Corner accent */}
        <div className="absolute top-8 md:top-12 right-8 md:right-12 border-t border-r border-primary/30 w-16 h-16 md:w-24 md:h-24 hover:scale-[1.2] transition-transform duration-500" />

        {/* Taglines */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
          <div className="relative h-24 md:h-32 w-full flex items-center justify-center">
            {taglines.map((tl, i) => (
              <h3
                key={i}
                className={`absolute text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-cream text-center px-6 transition-all duration-700 ${lang === "en" ? "font-houstiq tracking-wide" : "font-tajawal"} ${
                  i === currentTagline
                    ? "opacity-100 translate-y-0 scale-100 blur-0"
                    : "opacity-0 -translate-y-10 scale-[1.02] blur-sm"
                }`}
                style={{ filter: i === currentTagline ? "blur(0px)" : "blur(8px)", textShadow: "0 2px 24px rgba(0,0,0,0.35)" }}
              >
                <span className="relative inline-block">
                  {t(tl.key)}
                  <span
                    className={`absolute -bottom-3 left-0 right-0 h-[2px] rounded-full bg-gradient-to-r from-transparent via-gold to-transparent transition-transform duration-1000 ease-out origin-center ${
                      i === currentTagline ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </span>
              </h3>
            ))}
          </div>

          {/* Gold vertical line */}
          <div
            ref={lineRef}
            className={`w-px h-20 md:h-24 origin-top mt-8 relative anim-scale-y-in ${lineInView ? "in-view" : ""}`}
            style={{ transitionDelay: "0.5s", transform: lineInView ? "scaleY(1)" : "scaleY(0)" }}
          >
            <div className="w-full h-full bg-gradient-to-b from-primary to-transparent anim-pulse-opacity" />
          </div>
        </div>

        {/* Progress dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-10">
          {taglines.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setCurrentTagline(i)}
              className="relative h-1 rounded-full overflow-hidden bg-cream/30 backdrop-blur-sm transition-all duration-500 hover:bg-cream/50"
              style={{ width: i === currentTagline ? 48 : 16 }}
            >
              {i === currentTagline && (
                <div
                  className={`absolute inset-0 bg-cream rounded-full ${lang === "ar" ? "origin-right" : "origin-left"}`}
                  style={{ animation: "growWidth 5s linear forwards", width: "100%" }}
                />
              )}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhoWeAreSection;
